import { useState } from "react";
import "../design/tokens.css";
import "../design/base.css";
import {
  Button,
  Chip,
  Panel,
  PipFilter,
  PipPicker,
  PipMeter,
  SearchInput,
  Sprite,
  StatusChip,
  TierBadge,
} from "../design/primitives";
import type { DiffMode } from "../library/gridState";
import { art, cardSprite, sheetFor } from "../design/sprites";
import { CARD_WASH, shade } from "../design/shade";

/**
 * Dev-only component gallery (Phase 1, task 5). Not routed in production - see
 * the branch in main.tsx, which task 8 removes along with the old admin UI.
 *
 * The tints below are stand-ins so the sprites have something to be. Real
 * tints come from the creator's Games list (§12, NFR-13), never from code.
 */
const SAMPLE_TINTS = [
  ["#6FA86A", "green"],
  ["#8A7FB0", "violet"],
  ["#4A9B9B", "teal"],
  ["#D4A24A", "gold"],
  ["#B0899A", "rose"],
  ["#5E86B8", "blue"],
] as const;

const COLORS = [
  "--void", "--void-2", "--void-3", "--panel", "--panel-2",
  "--cream", "--cream-dim", "--cream-shade",
  "--maroon", "--maroon-text",
  "--periwinkle", "--teal",
  "--gold", "--gold-lit", "--gold-shade", "--gold-ink",
  "--paper", "--desk", "--staff",
];

const H2: React.CSSProperties = {
  fontFamily: "var(--font-display)",
  fontWeight: 700,
  fontSize: "var(--type-section-header)",
  letterSpacing: "var(--track-section-header)",
  color: "#fff",
  textShadow:
    "2px 0 0 #0A0A0B, -2px 0 0 #0A0A0B, 0 2px 0 #0A0A0B, 0 -2px 0 #0A0A0B, 2px 2px 0 #0A0A0B, -2px 2px 0 #0A0A0B, 2px -2px 0 #0A0A0B, -2px -2px 0 #0A0A0B, 4px 4px 0 #2A2A3E",
  margin: "0 0 var(--space-20)",
};

const LABEL: React.CSSProperties = {
  fontFamily: "var(--font-mono)",
  fontSize: "var(--type-meta-label)",
  letterSpacing: "var(--track-meta-label)",
  color: "var(--periwinkle)",
};

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section style={{ marginBottom: "var(--space-60)" }}>
      <h2 style={H2}>{title}</h2>
      {children}
    </section>
  );
}

const Row = ({ children }: { children: React.ReactNode }) => (
  <div style={{ display: "flex", flexWrap: "wrap", gap: "var(--space-20)", alignItems: "center" }}>
    {children}
  </div>
);

export default function Gallery() {
  const [diffValue, setDiffValue] = useState(0);
  const [diffMode, setDiffMode] = useState<DiffMode>("lte");
  const [rating, setRating] = useState(3);
  const [franchise, setFranchise] = useState("ZELDA");
  const [query, setQuery] = useState("");

  // The old CRA index.css still sets `body { font-family: 'Roboto' }`, and load
  // order between it and base.css is not something to depend on. Set it here
  // explicitly; task 8 deletes index.css.
  return (
    <main
      style={{
        padding: "var(--space-40) var(--gutter-desktop)",
        maxWidth: 1440,
        margin: "0 auto",
        fontFamily: "var(--font-body)",
        background: "var(--void)",
        color: "var(--cream)",
      }}
    >
      <h1
        style={{
          ...H2,
          fontSize: "var(--type-page-title)",
          textShadow: H2.textShadow?.toString().replace("4px 4px 0 #2A2A3E", "5px 5px 0 #2A2A3E"),
        }}
      >
        PERKS COMPONENT GALLERY
      </h1>
      <p style={{ ...LABEL, marginTop: 0, marginBottom: "var(--space-60)" }}>
        DESIGN_SYSTEM.md §1–7 · PHASE 1 TASK 5 · NOT A PRODUCT SCREEN
      </p>

      <Section title="COLOR TOKENS">
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))", gap: "var(--space-12)" }}>
          {COLORS.map((token) => (
            <div key={token} style={{ border: "2px solid var(--void)", background: "var(--panel)" }}>
              <div style={{ height: 46, background: `var(${token})` }} />
              <div style={{ ...LABEL, padding: "var(--space-8)" }}>{token}</div>
            </div>
          ))}
        </div>
        <Panel style={{ marginTop: "var(--space-20)", padding: "var(--space-16)" }}>
          <div style={LABEL}>THE TWO MAROONS ARE NOT INTERCHANGEABLE (D-14)</div>
          <p style={{ color: "var(--maroon)", fontFamily: "var(--font-mono)", fontSize: 14, margin: "var(--space-10) 0 0" }}>
            --maroon on --panel · 2.16:1 · this is what error text used to look like
          </p>
          <p style={{ color: "var(--maroon-text)", fontFamily: "var(--font-mono)", fontSize: 14, margin: "var(--space-6) 0 0" }}>
            --maroon-text on --panel · 6.21:1 · this is what it looks like now
          </p>
        </Panel>
      </Section>

      <Section title="TYPE SCALE">
        <div style={{ display: "grid", gap: "var(--space-12)" }}>
          <div style={{ fontFamily: "var(--font-display)", fontWeight: 700, fontSize: "var(--type-hero)", lineHeight: "var(--type-hero-lh)", color: "#fff", textShadow: H2.textShadow }}>
            HERO 62
          </div>
          <div style={{ fontFamily: "var(--font-display)", fontWeight: 700, fontSize: "var(--type-page-title)", color: "#fff", textShadow: H2.textShadow }}>
            PAGE TITLE 38
          </div>
          <div style={{ fontFamily: "var(--font-body)", fontSize: "var(--type-hero-paragraph)", lineHeight: "var(--type-hero-paragraph-lh)", color: "var(--cream-dim)", maxWidth: 620 }}>
            Hero paragraph, Work Sans 18/1.6 in --cream-dim. Plain, warm, slightly wry — a
            guitarist, not a SaaS company. No exclamation marks.
          </div>
          <div style={{ fontFamily: "var(--font-body)", fontWeight: 600, fontSize: "var(--type-card-title)" }}>
            Card title — Work Sans 16.5/600
          </div>
          <div style={{ fontFamily: "var(--font-mono)", fontSize: "var(--type-meta-value)", color: "var(--cream-dim)" }}>
            METADATA VALUE · JETBRAINS MONO 14
          </div>
          <div style={LABEL}>METADATA LABEL · MONO 11 · .08EM · PERIWINKLE</div>
        </div>
      </Section>

      <Section title="BUTTONS">
        <Row>
          <Button variant="gold" size="landing">SIGN IN WITH PATREON</Button>
          <Button variant="gold" size="state">TRY AGAIN</Button>
          <Button variant="gold" size="rail">DOWNLOAD PDF</Button>
        </Row>
        <div style={{ height: "var(--space-20)" }} />
        <Row>
          <Button variant="secondary" size="rail">DOWNLOAD GUITAR PRO</Button>
          <Button variant="secondary" size="rail">KEEP IT</Button>
          <Button variant="destructive" size="rail">DELETE</Button>
          <Button variant="gold" size="rail" disabled>DISABLED</Button>
        </Row>
        <p style={{ ...LABEL, marginTop: "var(--space-16)" }}>
          HOLD ONE DOWN — IT SHOULD JUMP 3PX AND THE BEVEL SHOULD INVERT, WITH NO EASING
        </p>
      </Section>

      <Section title="PIP METER (§4)">
        <Row>
          {[10, 11, 14, 20].map((size) => (
            <div key={size}>
              <div style={LABEL}>{size}PX</div>
              <div style={{ marginTop: 8 }}>
                <PipMeter value={3} size={size} />
              </div>
            </div>
          ))}
          <div>
            <div style={LABEL}>WITH RATIO</div>
            <div style={{ marginTop: 8 }}>
              <PipMeter value={4} size={14} showRatio />
            </div>
          </div>
        </Row>
        <div style={{ height: "var(--space-26)" }} />
        {/*
          Two controls since DF-D3, because they mean different things: one sets
          a value, the other describes a band. The filter's lit pips are exactly
          the arrangements it would show - switch the mode and watch them move.
        */}
        <div style={LABEL}>AS A FILTER — THE LIT PIPS ARE THE BAND THAT PASSES</div>
        <div style={{ marginTop: 10, display: "flex", gap: "var(--space-16)", alignItems: "center" }}>
          <div style={{ display: "flex" }}>
            {(["lte", "eq", "gte"] as const).map((m) => (
              <button
                key={m}
                type="button"
                aria-pressed={diffMode === m}
                onClick={() => setDiffMode(m)}
                style={{
                  fontFamily: "var(--font-mono)",
                  fontSize: "var(--type-chip)",
                  padding: "8px 14px",
                  border: "var(--border-structure) solid var(--void)",
                  background: diffMode === m ? "var(--gold)" : "var(--panel)",
                  color: diffMode === m ? "var(--gold-ink)" : "var(--periwinkle)",
                  cursor: "pointer",
                }}
              >
                {m === "lte" ? "\u2264" : m === "gte" ? "\u2265" : "="}
              </button>
            ))}
          </div>
          <PipFilter value={diffValue} mode={diffMode} onChange={setDiffValue} />
        </div>
        <div style={{ height: "var(--space-26)" }} />
        <div style={LABEL}>AS A PICKER — SETS A VALUE, CLAIMS NO DIRECTION</div>
        <div style={{ marginTop: 10 }}>
          <PipPicker value={rating} onChange={setRating} />
        </div>
      </Section>

      <Section title="CHIPS, BADGES, STATUS">
        <Row>
          {["ZELDA", "MOTHER", "TWIN PEAKS", "FINAL FANTASY"].map((f) => (
            <Chip key={f} active={franchise === f} onClick={() => setFranchise(f)}>
              {f}
            </Chip>
          ))}
        </Row>
        <div style={{ height: "var(--space-20)" }} />
        <Row>
          <TierBadge name="16 BIT" />
          <TierBadge name="64 BIT" />
          <StatusChip status="published" />
          <StatusChip status="draft" />
        </Row>
      </Section>

      <Section title="SEARCH (§7) — PRESS / ANYWHERE ON THIS PAGE">
        <SearchInput value={query} onChange={(e) => setQuery(e.target.value)} />
        <p style={{ ...LABEL, marginTop: "var(--space-12)" }}>
          {query ? `TYPED: ${query.toUpperCase()}` : "0 OF 47 TABS"}
        </p>
      </Section>

      <Section title="PANELS (§3.1)">
        <Row>
          <Panel style={{ padding: "var(--space-22)", width: 300 }}>
            <div style={LABEL}>RAISED PANEL</div>
            <p style={{ margin: "var(--space-10) 0 0", color: "var(--cream-dim)", fontSize: "var(--type-note-body)" }}>
              4px hard shadow, periwinkle inner bevel top-left, void bevel bottom-right.
            </p>
          </Panel>
          <Panel flush style={{ padding: "var(--space-22)", width: 300 }}>
            <div style={LABEL}>FLUSH PANEL</div>
            <p style={{ margin: "var(--space-10) 0 0", color: "var(--cream-dim)", fontSize: "var(--type-note-body)" }}>
              Same bevel, no hard shadow — for panels nested inside another.
            </p>
          </Panel>
        </Row>
      </Section>

      <Section title="SPRITES (§5) — BUILT AT MODULE INIT, BLOB URLS">
        <Row>
          {SAMPLE_TINTS.map(([tint, name]) => (
            <div key={tint} style={{ textAlign: "center" }}>
              <div style={{ background: shade(tint, CARD_WASH), padding: 18 }}>
                <Sprite src={sheetFor(tint)} size={132} />
              </div>
              <div style={{ ...LABEL, marginTop: 8 }}>SHEET · {name.toUpperCase()}</div>
            </div>
          ))}
        </Row>
        <div style={{ height: "var(--space-26)" }} />
        <Row>
          {(
            [
              ["CASSETTE", art.cassette],
              ["QBLOCK", art.qblock],
              ["CRT", art.crt],
              ["CRTERR", art.crtErr],
              ["LOCK", art.lock],
              ["SADNOTE", art.note],
            ] as const
          ).map(([name, src]) => (
            <div key={name} style={{ textAlign: "center" }}>
              <Sprite src={src} size={104} />
              <div style={{ ...LABEL, marginTop: 8 }}>{name}</div>
            </div>
          ))}
          <div style={{ textAlign: "center" }}>
            <Sprite src={art.pdf} size={48} label="PDF" />
            <div style={{ ...LABEL, marginTop: 8 }}>PDFICO</div>
          </div>
          <div style={{ textAlign: "center" }}>
            <Sprite src={art.gp} size={48} label="Guitar Pro" />
            <div style={{ ...LABEL, marginTop: 8 }}>GPICO</div>
          </div>
        </Row>
        <div style={{ height: "var(--space-26)" }} />
        <div style={LABEL}>THE FOUR CARD OBJECTS, ALL TINTED THE SAME GREEN (D-10)</div>
        <div style={{ height: "var(--space-12)" }} />
        <Row>
          {(["SHEET", "CASSETTE", "CRT", "QBLOCK"] as const).map((object) => (
            <div key={object} style={{ textAlign: "center" }}>
              <div style={{ background: shade("#6FA86A", CARD_WASH), padding: 18 }}>
                <Sprite src={cardSprite(object, "#6FA86A")} size={104} />
              </div>
              <div style={{ ...LABEL, marginTop: 8 }}>{object}</div>
            </div>
          ))}
        </Row>
      </Section>
    </main>
  );
}
